import React from 'react'
import { View, StyleSheet } from 'react-native'
import { CheckBox, Text } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { useColors } from '../utils/useColors'

/**
 * WA-ONBOARD-001 — an opt-in checkbox with its disclosure attached.
 *
 * Separate from the funnel's other field renderers on purpose. Every policy-critical property of a
 * consent control is a RENDERING property, and none of them is visible from the funnel structure or
 * from the payload the funnel eventually posts:
 *
 *   * `checked={value === true}` — not `!!value`, not `value ?? props.default`. Only a literal
 *     `true` may draw a ticked box, so an untouched field (react-hook-form leaves it `undefined`)
 *     and any stray truthy value both render as what they are: not agreed.
 *   * the disclosure renders NEXT TO the label rather than living in a translation file that
 *     nothing displays. Consent is consent to what was on screen.
 *   * `onChange` forwards the boolean unchanged, so "unticked" leaves here as `false`.
 *
 * A default value is deliberately NOT a prop. There is no correct default for a consent control
 * other than "not given", and a component that can be told otherwise is a component that will be.
 */
export default function ConsentCheckbox ({ value, onChange, label, disclosure }) {
  const { t } = useTranslation()
  const colors = useColors()

  return (
    <View style={styles.container}>
      <CheckBox
        checked={value === true}
        // The instant is reported ALONGSIDE the answer, from here, because here is the only place
        // that knows when the merchant acted. The funnel's submit is three screens later (email,
        // password), so a timestamp taken there is systematically late for a field whose entire
        // purpose is to say when consent was given.
        onChange={(checked) => onChange(checked, new Date().toISOString())}
      >
        {() => <Text style={styles.label}>{t(label)}</Text>}
      </CheckBox>
      {disclosure ? (
        <Text category='c1' style={[styles.disclosure, { color: colors.mediumGrey }]}>
          {t(disclosure)}
        </Text>
      ) : null}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 20,
  },
  label: {
    marginLeft: 10,
    flexShrink: 1,
  },
  disclosure: {
    marginTop: 8,
    opacity: 0.7,
  },
})
